import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { vars, breakpoints, Heading, Inline } from '@etg/wings';
import { Collection, DevBar } from '@eti/debug';
import { useProperty, useSearch } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { SearchLocation, SelfServiceRebookingTraveler } from '@eti/types';
import { Profiler } from '@eti/utils';
import { useHistory } from 'react-router-dom';
import { createSearchUrl } from '../../widgets/search-form/utils/createSearchUrl';
import { routes } from '../../constants/routesAndApisConstants';
import { DebugCommonComponents } from '../debug/components';
import { isDev } from '../debug/utils';
import { getSearchFormDataFrom } from '../../widgets/search-form/utils/initialValues';
import SearchForm from './SearchFormNewContainer';
import SearchFormCard from './SearchFormCard';
import SearchFormSection from './SearchFormSection';

const contentStyles = css`
  display: flex;
  flex: 1;
  justify-content: center;
  max-width: 26.25rem;
  text-align: center;
`;

const headingStyles = css`
  color: ${vars.colors.panel.foreground};

  @media (min-width: ${breakpoints._768}) {
    color: #fff;
    text-shadow: 0 0 10px #000;
  }
`;

const carrierLogoStyles = css`
  height: 100px;
`;

const SearchFormPanel = ({
  carrierLogoUrl,
  destination,
  isPopunderEnabled,
  origin,
  panelHeaderTitle,
  rebookingData = null,
  setValueRef,
}) => {
  const history = useHistory();
  const { p } = useProperty();
  const { currentSearchData } = useSearch();
  const search = getSearchFormDataFrom(currentSearchData);

  useEffect(() => {
    const loadResultPage = async () => {
      if (origin?.value && destination?.value) {
        const ResultPage = await import('../../pages/result');
        ResultPage.default.preload();
      }
    };

    loadResultPage();
  }, [origin, destination]);

  const isDestinationPage = history?.location?.pathname === routes.DESTINATION;

  const handleSearch = (searchParameters, searchData) => {
    sessionStorage.removeItem('hasPaymentFailed');

    const aid = p('SearchForm.Popunder.BookingCom.Aid');
    const searchUrl = createSearchUrl(searchData);

    if (isPopunderEnabled && searchUrl && aid) {
      const { adults, childAges } = searchData.travelers;

      const url = new URL('https://sp.booking.com/searchresults.html');
      url.searchParams.set('aid', String(aid));
      url.searchParams.set('checkin', searchData.routes[0].departureDate);
      url.searchParams.set('checkout', searchData.routes.at(-1).departureDate);
      url.searchParams.set('group_adults', String(adults));
      url.searchParams.set('iata', searchData.routes[0].destinationCode);
      url.searchParams.set('iata_orr', '1');
      url.searchParams.set('label', 'search_extention');

      if (childAges.length > 0) {
        const zeroIndex = childAges.indexOf(0);
        const ages = zeroIndex > -1 ? childAges.slice(0, zeroIndex) : childAges;

        url.searchParams.set('group_children', String(childAges.length));
        ages.forEach((age) => url.searchParams.append('age', String(age)));
      }

      window.open(searchUrl, '_blank');
      window.location.href = url.toString();
      return;
    }

    if (p('IbeClient.RedirectCustomerToAnotherSite.Enabled')) {
      history.push(routes.NEW_BRAND_REDIRECTION, { searchVariables: searchParameters });
    } else {
      history.push(routes.RESULT, {
        searchVariables: searchParameters,
        fromDestinationPage: isDestinationPage,
      });
    }
  };

  return (
    <>
      <SearchFormSection>
        <Inline
          align="center"
          alignY="center"
          collapseBelow={breakpoints._768}
          spacing={{ [breakpoints._0]: 24, [breakpoints._768]: 32 }}
        >
          <Heading
            className={cx(headingStyles, contentStyles)}
            data-testid="searchFormPanel-header"
            level={1}
          >
            {panelHeaderTitle}
          </Heading>
          {carrierLogoUrl && (
            <div className={contentStyles}>
              <img
                alt={panelHeaderTitle}
                className={carrierLogoStyles}
                data-testid="searchForm-carrierLogo-image"
                src={carrierLogoUrl}
              />
            </div>
          )}
        </Inline>
        <SearchFormCard data-testid="searchPage-searchForm">
          <Profiler>
            <SearchForm
              onSearch={handleSearch}
              rebookingData={rebookingData}
              search={search}
              setValueRef={setValueRef}
              shouldShowRebookingBanner
            />
          </Profiler>
        </SearchFormCard>
      </SearchFormSection>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

SearchFormPanel.propTypes = {
  carrierLogoUrl: PropTypes.string,
  destination: SearchLocation,
  isPopunderEnabled: PropTypes.bool,
  origin: SearchLocation,
  panelHeaderTitle: PropTypes.string,
  rebookingData: PropTypes.shape({
    criteria: PropTypes.arrayOf(PropTypes.string),
    formConstraints: PropTypes.shape({
      passengers: PropTypes.shape({
        isDisabled: PropTypes.bool,
        maxNumberOfTravelers: PropTypes.number,
      }),
    }),
    initialValues: PropTypes.shape({
      passengers: PropTypes.shape({
        adults: PropTypes.number,
        children: PropTypes.number,
        infants: PropTypes.number,
      }),
    }),
    travelers: PropTypes.arrayOf(SelfServiceRebookingTraveler),
  }),
  setValueRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default SearchFormPanel;
